<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { getDisplayName } from "../../utils/GeneralUtils.ts";
import { i18n } from "../../main.ts";

const props = defineProps({
    productNo: {
        type: String,
        required: true,
    },
    productName: {
        type: String,
        required: true,
    },
    profileLanguages: {
        type: Array as () => string[],
        default: () => [],
    },
    questionnaireLanguages: {
        type: Array as () => string[],
        default: () => [],
    },
    trainerLanguage: {
        type: String,
        default: "",
    },
});

const { t, locale } = useI18n();

const popoverRef = ref<any>(null);

const imageBasePath = "./images/profile-cover/";
const resolvedImageUrl = ref<string | null | undefined>(undefined);

function preloadImage(): void {
    const isDE = locale.value.startsWith("de");
    const imgLocale = isDE ? "de" : "en";
    const tryDeFallbackPng = () => {
        const img = new Image();
        img.onload = () => { resolvedImageUrl.value = img.src; };
        img.onerror = () => { resolvedImageUrl.value = null; };
        img.src = `${imageBasePath}de-${props.productNo}.png`;
    };
    const tryDeFallback = () => {
        const img = new Image();
        img.onload = () => { resolvedImageUrl.value = img.src; };
        img.onerror = tryDeFallbackPng;
        img.src = `${imageBasePath}de-${props.productNo}.jpg`;
    };
    const tryPng = () => {
        const img = new Image();
        img.onload = () => { resolvedImageUrl.value = img.src; };
        img.onerror = () => {
            if (isDE) {
                resolvedImageUrl.value = null;
            } else {
                tryDeFallback();
            }
        };
        img.src = `${imageBasePath}${imgLocale}-${props.productNo}.png`;
    };
    const imgJpg = new Image();
    imgJpg.onload = () => { resolvedImageUrl.value = imgJpg.src; };
    imgJpg.onerror = tryPng;
    imgJpg.src = `${imageBasePath}${imgLocale}-${props.productNo}.jpg`;
}

const hasLanguages = props.profileLanguages.length > 0 || props.questionnaireLanguages.length > 0;

onMounted(preloadImage);
</script>

<template>
    <Button
        icon="pi pi-info-circle"
        text
        rounded
        size="small"
        style="width: 2rem; height: 2rem; min-width: 2rem; padding: 0; flex-shrink: 0;"
        :aria-label="t('profile-overview.productNumber')"
        @click="popoverRef?.toggle($event)"
    />
    <Popover ref="popoverRef" class="profile-cover-popover">
        <div class="popover-inner">
            <img
                v-if="resolvedImageUrl"
                :src="resolvedImageUrl"
                :alt="productName"
                class="cover-image"
            />
            <div class="font-bold text-center">{{ productName }}</div>
            <div class="text-sm font-light text-center">
                {{ t("profile-overview.productNumber") }}: {{ productNo.toUpperCase() }}
            </div>

            <!-- Languages section -->
            <template v-if="hasLanguages">
                <Divider class="my-2" />
                <div v-if="profileLanguages.length" class="lang-row">
                    <span class="lang-label" v-tooltip="t('profile-overview.languages.profile')">
                        <i class="pi pi-book" />
                        <span class="lang-label-text">{{ t('profile-overview.languages.profile') }}</span>
                    </span>
                    <div class="flex gap-1 flex-wrap">
                        <Tag
                            v-for="lang in [...profileLanguages].sort()"
                            :key="lang"
                            rounded
                            style="cursor: default"
                            :pt="lang === trainerLanguage
                                ? { root: { style: { background: 'var(--primary-color)', color: 'var(--p-surface-100)', borderRadius: '3px' } } }
                                : { root: { style: { background: 'var(--p-surface-200)', color: 'var(--p-surface-500)', borderRadius: '3px' } } }"
                            v-tooltip.bottom="getDisplayName(i18n.global.locale.value, lang)"
                        >{{ lang }}</Tag>
                    </div>
                </div>
                <div v-if="questionnaireLanguages.length" class="lang-row mt-1">
                    <span class="lang-label" v-tooltip="t('profile-overview.languages.questionnaire')">
                        <i class="pi pi-pen-to-square" />
                        <span class="lang-label-text">{{ t('profile-overview.languages.questionnaire') }}</span>
                    </span>
                    <div class="flex gap-1 flex-wrap">
                        <Tag
                            v-for="lang in [...questionnaireLanguages].sort()"
                            :key="lang"
                            rounded
                            style="cursor: default"
                            :pt="lang === trainerLanguage
                                ? { root: { style: { background: 'var(--primary-color)', color: 'var(--p-surface-100)', borderRadius: '3px' } } }
                                : { root: { style: { background: 'var(--p-surface-200)', color: 'var(--p-surface-500)', borderRadius: '3px' } } }"
                            v-tooltip.bottom="getDisplayName(i18n.global.locale.value, lang)"
                        >{{ lang }}</Tag>
                    </div>
                </div>
            </template>
        </div>
    </Popover>
</template>

<style scoped>
.profile-cover-popover :deep(.p-popover-content) {
    padding: 0.75rem;
}

.popover-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: min(360px, 85vw);
    max-height: min(80vh, 560px);
    overflow-y: auto;
    overscroll-behavior: contain;
}

.cover-image {
    width: 100%;
    border-radius: 4px;
}

.lang-row {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
}

.lang-label {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    color: var(--p-text-muted-color);
}

.lang-label-text {
    display: block;
}
</style>
